import { incrementSessionDepth, devtools } from '../../lib/utils';
import { getAndIncrementPageview, pageviewInactivityMs } from '../../lib/pageview_counter';
import { activeGroupsGrantAnalytics } from '../../lib/onetrust_consent';
import NativeAds from '../../lib/native_ads';
import Slots from '../../lib/slots';
import SlotNames from '../../lib/slot_names';
import Logger from '../../lib/logger';
import Lifecycle from '../../lib/lifecycle';
import PluginLoader from '../../lib/plugin_loader';
import PageMetaData from '../../lib/page_meta_data';
import store from '../../lib/store';
import settings from '../../lib/settings';
import { installAdBlockerDetection } from '../../lib/ads_blocker_detector';
import Metrics from '../../lib/metrics';

export function initMixin(ConcertAds) {
  ConcertAds.prototype._init = function(config = {}) {
    // Resets the store. Particularly useful for tests.
    store.reset();
    // Define top-level settings getter and setters on the app instance.
    // This allows us to leverage the CascadingConfig of settings,
    // but without modifying the entire library to use its API.
    Object.defineProperty(this, 'settings', {
      get() {
        return settings.get();
      },
      set(value) {
        settings.set(value);
      },
    });

    settings.add({
      prebid: {},
      slots: [],
      classifiers: [],
      ...config,
    });

    this.slots = new Slots({ app: this });
    this.slotNames = new SlotNames();
    this.logger = Logger;
    this.lifecycle = new Lifecycle();
    this.pageMetaData = new PageMetaData({ app: this });
    this.plugins = new PluginLoader({ app: this });
    this.beforeAdsRequested = [];
    // Load promise per active dynamic plugin, keyed by module name (see
    // plugins/dynamic). A slot's render() awaits only the plugins it actually
    // needs (e.g. an adhesion slot awaits the adhesion chunk), rather than every
    // slot blocking on a shared list — so a non-adhesion slot is never delayed by
    // the adhesion library.
    this.dynamicPlugins = {};
    // Registry of one-shot display gates, keyed by category (e.g. 'firstSlot').
    // See the display_control mixin: blockUntilDisplay / releaseDisplay / gatesBlocking.
    this.displayGates = new Map();
    // External gates that finalizeSlots() has declared done: they no longer pause the first-slot
    // backstops even if still pending. A gate armed after that is unmarked, so it holds again until the
    // next finalizeSlots() or its own release. Replaced when install() starts a new cycle.
    this.finalizedExternalGates = new WeakSet();
    // Set once finalizeSlots() has run, so code that starts late (e.g. a dynamic plugin whose chunk
    // resolves after the page finalized) can tell it already missed `slotsFinalized`.
    this.hasFinalizedSlots = false;

    // ADS-65: count real boots only (not gallery / reinstall). Publish in setUpInitialVariables.
    // `pageviewCounted` records whether consent was known now: when it is not, the OneTrust plugin
    // does the delayed count once C0002 arrives, so a late CMP neither loses this page's count nor
    // resets the stored session depth. This runs BEFORE createCommandQueue so a queued `cmd` that
    // installs synchronously (firing the OneTrust plugin's delayed-count path) cannot count this
    // page before `_init` has, which would double-count it.
    const pageviewConsentGranted = activeGroupsGrantAnalytics(window.OnetrustActiveGroups);
    this.pageviewCounted = pageviewConsentGranted;
    this.pageview = getAndIncrementPageview({
      canPersist: pageviewConsentGranted,
      inactivityMs: pageviewInactivityMs(this.settings.pageviewInactivityMinutes),
    });

    createCommandQueue(this);
    incrementSessionDepth();

    NativeAds.listenForHymnalRenderEvents(this);

    installAdBlockerDetection().then(isBlocked => {
      this.adsBlocked = isBlocked; // this supports external code that needs to know if an adblocker is running.
      if (isBlocked) {
        Metrics.track('Ads Blocked', true);
      }
      if (process.env.NODE_ENV === 'test') {
        window.dispatchEvent(new Event('adBlockDetectorReady')); // this event exists to support tests only
      }
    });

    if (devtools) {
      devtools.emit('init', this);
    }
  };
}

/**
 * Establishes the command queue and fires initial commands passed.
 *
 * @param {ConcertAds} app
 */
function createCommandQueue(app) {
  app.commandQueue = app.settings.cmd || [];
  app.commandQueue.push = fn => {
    if (typeof fn === 'function') {
      try {
        fn.call(this, app);
      } catch (e) {
        Logger.log('Error processing command: ' + e.message, {
          level: 'warn',
        });
      }
    }
  };

  // Fire any commands sent before instantiation
  app.commandQueue.forEach(fn => app.commandQueue.push(fn));
}
